import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation, useNavigate, useSearchParams } from "react-router";
import XSifterAppHeader from "./XSifterAppHeader";
import "./XSifterExternalPage.css"
import Container from "@mui/material/Container";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import UserAuthService from "../../services/UserAuthService";
import XSifterAppDashboard from "../XSifterAppDashboard";
import Alert from "@mui/material/Alert";
import XSifterQuery from "../XSifterAppQuery";
import { setDisplayMessage, setDisplayMessageType, setCurrentQueryId } from "../../store/XSifterAppReducer";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import XSifterAdminPage from "../admin/XSifterAdminPage";
import XSifterAdminService from "../../services/XSifterAdminService";
import XSifterQueryService from "../../services/XSifterQueryService";
import XSifterAppFooter from "./XSifterAppFooter";
import XSifterSavedQueryList from "../search/XSifterSavedQueryList";
import "./XSifterAppPage.css"
import XSifterNotifications from "../XSifterNotificationsList";
import XSifterRevision from "../search/XSifterQueryRevision";
import { IXSifterUser } from "../../types/XSifterAppUser";
import { useInterval } from "./UseInterval";

interface XSifterAppPageProps {
    
}

const XSifterAppPage: React.FC<XSifterAppPageProps> = (props: XSifterAppPageProps) => {
    const displayMessage = useAppSelector((state) => state.app.displayMessage);
    const displayMessageType = useAppSelector((state) => state.app.displayMessageTyoe);    
    const [currentUser, setCurrentUser] = useState<IXSifterUser | undefined>(undefined);
    const [headerRemountKey, setHeaderRemountKey] = useState(0);
    const [numUsersApproval, setNumUsersApproval] = useState<number>(0);
    const [showAck, setShowAck] = useState<boolean>(false);
    const userAuthService = UserAuthService.getInstance();
    const appUser: IXSifterUser | undefined = useAppSelector((state) => state.app.currentUser);
    const adminService = new XSifterAdminService();
    const queryService = new XSifterQueryService();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        userAuthService.setCurrentUser(appUser);

        if (!userAuthService.isLoggedIn()) {
            navigate('/');
        } else {
            // fetch the user once and derive approval from it, so a failed/early
            // fetch is never mistaken for an unapproved user
            userAuthService.getCurrentUser()
                .then((user) => {
                    if (!user) {
                        dispatch(setDisplayMessage("You have been logged out. Please log in again."));
                        dispatch(setDisplayMessageType("warning"));
                        navigate('/');
                        return;
                    }
                    userAuthService.setCurrentUser(user);
                    if (!userAuthService.isUserApproved(user)) {
                        navigate('/needs-approval');
                        return;
                    }
                    if (user.hasAck === false) {
                        setShowAck(true);
                    }
                    setCurrentUser(user);
                    if (userAuthService.isAdmin()) {
                        adminService.getNumNeedingApproval(appUser)
                            .then((response) => {
                                setNumUsersApproval(response.count);
                            }).catch((error) => {});
                    }
                }).catch((error) => {
                    dispatch(setDisplayMessage("You have been logged out. Please log in again."));
                    dispatch(setDisplayMessageType("warning"));
                    navigate('/');
                });
        }
    }, []);

    useEffect(() => {
        if (displayMessage && displayMessageType === 'success') {
            const autoClearDisplayMessage = setTimeout(() => {
                dispatch(setDisplayMessage(undefined));
                dispatch(setDisplayMessageType(undefined));
            }, 10000);
            return () => clearTimeout(autoClearDisplayMessage);
        }
    }, [displayMessage, displayMessageType]);

    useEffect(() => {
        // the query trace id only applies while on the query page
        if (!/^(\/app)?\/query\/?$/.test(location.pathname)) {
            queryService.setQueryTraceId(null);
            dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
        }
    }, [location.pathname]);

    useInterval(() => {
        const secondsUntilRenewal = userAuthService.getSecondsUntilRenewal();
        if (secondsUntilRenewal !== -1 && secondsUntilRenewal <= UserAuthService.TOKEN_RENEWAL_THRESHOLD_MS) {
            userAuthService.renewToken();
        }
    }, UserAuthService.TOKEN_RENEWAL_CHECK_INTERVAL_MS);

    const forceRemountHeader = () => {
        userAuthService.getCurrentUser()
            .then((user) => {
                userAuthService.setCurrentUser(user);
                if (user?.hasAck === false) {
                    setShowAck(true);
                }
                
                setCurrentUser(user);
                setHeaderRemountKey(headerRemountKey + 1);
                if (userAuthService.isAdmin()) {
                    adminService.getNumNeedingApproval(appUser)
                        .then((response) => {
                            setNumUsersApproval(response.count);
                        }).catch((error) => {});
                }
            }).catch(() => {
                dispatch(setDisplayMessage("You have been logged out. Please log in again."));
                dispatch(setDisplayMessageType("warning"));
                navigate('/');
            })
    }

    const alertType = displayMessageType || "success";    

    const clearDisplayMessage = () => {
        dispatch(setDisplayMessage(undefined));
        dispatch(setDisplayMessageType(undefined));
    }

    const renderAlert = () => {
        if (!displayMessage) {
            return (<></>);
        }

        return (
            <Alert severity={alertType} 
                action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                clearDisplayMessage();
                            }}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>                
                }
            >
                {displayMessage}
            </Alert>
        );
    }

    const onAckClicked = () => {
        userAuthService.acknowledgeRules()
            .then((response) => {
                setShowAck(false);
            })
            .catch((error) => {
                dispatch(setDisplayMessage("Could not process acknowledgement. Please try again."));
                dispatch(setDisplayMessageType("warning"));
            });
    }

    return (
        <>
        <XSifterAppHeader key={headerRemountKey} currentUser={appUser} numAccountsNeedingApproval={numUsersApproval} />
        <Container className={"xxsifterpagecontent"} sx={{ marginTop: "16px" }}>
            { renderAlert() }
            <Routes>
                {currentUser && <Route path="query" element={<XSifterQuery key={location.key} currentUser={currentUser} onForceHeaderRemount={forceRemountHeader} />} />}
                <Route path="notifications" element={<XSifterNotifications currentUser={currentUser} onForceHeaderRemount={forceRemountHeader} />} />
                <Route path="saved" element={<XSifterSavedQueryList />} />
                <Route path="refinequery" element={<XSifterRevision currentUser={currentUser} savedQueryId={searchParams.get('queryId')} onForceHeaderRemount={forceRemountHeader} />} />
                <Route path="admin/*" element={<XSifterAdminPage onForceHeaderRemount={forceRemountHeader} />} />
                <Route path="*" element={<XSifterAppDashboard />} />
            </Routes>
            <Dialog open={showAck}>
                <DialogTitle>Acknowledgement</DialogTitle>
                <DialogContent dividers>
                    <p>By using XSifter, you understand and agree to the following:</p>
                    <ul>
                        <li>You agree to abide by the <a href="https://help.x.com/en/rules-and-policies" target="_blank" rel="noreferrer">X Rules and Policies</a>.</li>
                        <li>You have read and agree to the <a href="https://docs.x.com/developer-terms/policy" target="_blank" rel="noreferrer">X Developer Policy</a> rules.</li>
                        <li>You agree to not violate the terms of the X API by not using any <a href="https://docs.x.com/developer-terms/restricted-use-cases" target="_blank" rel="noreferrer">restricted uses of the X API</a>.</li>
                        <li>You understand that any data downloaded by XSifter will only be placed in your DiscoverText account. No data can be directly downloaded offline.</li>
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onAckClicked}>Acknowledge</Button>
                </DialogActions>            
            </Dialog>
        </Container>
        <XSifterAppFooter />
        </>
    );
}

export default XSifterAppPage;
